import { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { GraduationCap, Search, X, FileText, Video, Headphones, Image as ImageIcon, UserPlus, AlertTriangle, CheckCircle2, Users, ExternalLink, ArrowUp, ArrowDown, Languages, MessageCircle, Copy, Radio } from 'lucide-react';
import TranslationPrewarmWidget from './TranslationPrewarmWidget';
import SopListenPills from './SopListenPills';
import RadioSendModal from './RadioSendModal';

const CAT_COLOURS = ['#2A7B88', '#378ADD', '#BA7517', '#7C3AED', '#D4726A', '#2D8B5E', '#F59E0B', '#EC4899', '#0EA5E9', '#1B2A4A', '#6B7280', '#8B5CF6'];

function mediaIcon(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('video')) return <Video size={12} />;
  if (t.includes('audio')) return <Headphones size={12} />;
  if (t.includes('image')) return <ImageIcon size={12} />;
  return <FileText size={12} />;
}

export default function DivisionTrainingCenter({ divisionId, isCEO = false }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [outlet, setOutlet] = useState('');
  const [status, setStatus] = useState('');
  const [openSop, setOpenSop] = useState(null);
  const [assignPerson, setAssignPerson] = useState(false);
  // Column sort state for the SOP table
  const [sort, setSort] = useState({ key: 'title', dir: 'asc' });

  const refresh = async () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (cat) params.set('category', cat);
    if (outlet) params.set('outlet', outlet);
    if (status) params.set('status', status);
    const r = await api.get(`/outlet-intel/training/division/${divisionId}?${params.toString()}`);
    setData(r.data);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [divisionId, cat, outlet, status]);

  // Debounced search
  useEffect(() => {
    const h = setTimeout(refresh, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line
  }, [q]);

  // Hide cost-centres + coming-soon outlets from Completion-by-Outlet card grid.
  const visibleOutletCompletion = useMemo(() => {
    if (!data) return [];
    return (data.outlet_completion || []).filter(oc => oc.sops > 0);
  }, [data]);

  // Sort applied client-side to the SOP list.
  const sortedSops = useMemo(() => {
    if (!data) return [];
    const arr = [...data.sops];
    const dir = sort.dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
    return arr;
  }, [data, sort]);

  const toggleSort = (key) => {
    setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  if (!data) return <div className="p-6 text-sm" style={{ color: '#6B7280' }}>Loading training library…</div>;

  const clearFilters = () => { setCat(''); setOutlet(''); setStatus(''); setQ(''); };

  // Empty-state helper — tell user which filter is likely the problem.
  const emptyHint = () => {
    if (data.sops.length > 0) return null;
    const reasons = [];
    if (cat && outlet) reasons.push(`"${cat}" isn't tracked at ${outlet} — try clearing either filter.`);
    else if (cat) reasons.push(`No SOPs in "${cat}" match your search.`);
    else if (outlet) reasons.push(`No SOPs at ${outlet} match your search.`);
    else if (q) reasons.push(`No SOPs contain "${q}".`);
    else reasons.push('No SOPs exist yet.');
    return reasons[0];
  };

  const divLabel = {
    me: 'MindEscapes®',
    tdb: 'The Doggy Bakery',
    lc: 'Les Concierges',
    'cc-clubs': 'Club Concierge',
  }[divisionId] || 'Division';

  return (
    <div className="space-y-4" data-testid="division-training-center">
      {/* CEO-only translation cache widget (silent for other roles) */}
      <TranslationPrewarmWidget isCEO={isCEO} />

      {/* Header row */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-bold flex items-center gap-2" style={{ color: '#1B2A4A' }}>
            <GraduationCap size={18} style={{ color: '#2A7B88' }} />
            {divLabel} Training Library
          </h3>
          <p className="text-[11px]" style={{ color: '#6B7280' }}>Click any category or outlet to filter. Search by keyword. Click any SOP to read / edit / track.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setAssignPerson(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="assign-new-joiner-btn">
            <UserPlus size={12} /> Assign to New Joiner
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {[
          { label: 'Unique Modules', value: data.summary.unique_modules, accent: '#2A7B88' },
          { label: 'Total SOP Records', value: data.summary.total_sop_records, accent: '#1B2A4A' },
          { label: 'Cost Centres Covered', value: data.summary.outlets_covered, accent: '#BA7517' },
          { label: 'Media Attachments', value: data.summary.media_attachments, accent: '#2D8B5E' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl p-3 border" style={{ borderColor: '#E5E1DC' }}>
            <div className="w-6 h-[3px] rounded-sm mb-2" style={{ backgroundColor: s.accent }} />
            <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>{s.label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color: '#1B2A4A' }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Category chips — clickable */}
      <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
        <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>By Category · tap to filter</p>
        <div className="flex flex-wrap gap-1.5">
          {data.by_category.map((c, i) => (
            <button key={c.name} onClick={() => setCat(cat === c.name ? '' : c.name)}
              className="text-[11px] px-2.5 py-1 rounded-full font-medium transition" style={{
                backgroundColor: cat === c.name ? CAT_COLOURS[i % CAT_COLOURS.length] : '#F7F5F2',
                color: cat === c.name ? '#fff' : '#1B2A4A',
                border: `1px solid ${cat === c.name ? CAT_COLOURS[i % CAT_COLOURS.length] : '#E5E1DC'}`,
              }}
              data-testid={`cat-chip-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              {c.name} <strong>{c.count}</strong>
            </button>
          ))}
        </div>
      </div>

      {/* Outlet completion snapshot — only real operating outlets with SOPs */}
      {visibleOutletCompletion.length > 0 && (
        <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Completion by Outlet · tap to filter</p>
            <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{visibleOutletCompletion.length} outlets</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {visibleOutletCompletion.map((oc) => (
              <button key={oc.outlet} onClick={() => setOutlet(outlet === oc.outlet ? '' : oc.outlet)}
                className="text-left rounded-lg p-2.5 border hover:bg-[#F7F5F2] transition" style={{
                  borderColor: outlet === oc.outlet ? '#2A7B88' : '#E5E1DC',
                  backgroundColor: outlet === oc.outlet ? '#F0FDF4' : '#fff',
                }}
                data-testid={`outlet-completion-${oc.outlet.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{oc.outlet}</p>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold tabular-nums" style={{
                    backgroundColor: oc.completion_pct >= 80 ? '#ECFDF5' : oc.completion_pct >= 50 ? '#FFFBEB' : '#FEF2F2',
                    color: oc.completion_pct >= 80 ? '#166534' : oc.completion_pct >= 50 ? '#854F0B' : '#991B1B',
                  }}>{oc.completion_pct}%</span>
                </div>
                <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>{oc.sops} SOPs · {oc.headcount} staff · {oc.pending_people} with pending</p>
                <div className="h-1 rounded-full mt-1.5" style={{ backgroundColor: '#F3F4F6' }}>
                  <div className="h-full rounded-full" style={{ width: `${oc.completion_pct}%`, backgroundColor: oc.completion_pct >= 80 ? '#2D8B5E' : oc.completion_pct >= 50 ? '#BA7517' : '#D4726A' }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search + filters */}
      <div className="bg-white rounded-xl border p-3 flex flex-wrap items-center gap-2" style={{ borderColor: '#E5E1DC' }}>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-2 top-2.5" style={{ color: '#9CA3AF' }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search SOP title / content / category"
            className="w-full pl-7 pr-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }}
            data-testid="training-search" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} className="px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} data-testid="training-status-filter">
          <option value="">All statuses</option>
          <option value="Active">Active</option>
          <option value="Draft">Draft</option>
          <option value="Archived">Archived</option>
        </select>
        {(cat || outlet || status || q) && (
          <button onClick={clearFilters} className="flex items-center gap-1 px-2 py-1.5 rounded text-xs border" style={{ borderColor: '#E5E1DC', color: '#D4726A' }} data-testid="clear-filters">
            <X size={12} /> Clear
          </button>
        )}
        <p className="text-[10px] ml-auto tabular-nums" style={{ color: '#6B7280' }}>{data.sops.length} result{data.sops.length === 1 ? '' : 's'}</p>
      </div>

      {/* Active filter chips */}
      {(cat || outlet) && (
        <div className="flex flex-wrap gap-1.5 -mt-2">
          {cat && <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: '#E6F1FB', color: '#185FA5' }}>Category: <strong>{cat}</strong> <button onClick={() => setCat('')}><X size={10} /></button></span>}
          {outlet && <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: '#E6F1FB', color: '#185FA5' }}>Outlet: <strong>{outlet}</strong> <button onClick={() => setOutlet('')}><X size={10} /></button></span>}
        </div>
      )}

      {/* SOP table with sortable columns */}
      <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="training-sop-table">
            <thead className="sticky top-0 z-10" style={{ backgroundColor: '#F7F5F2' }}>
              <tr>
                <SortableTh label="SOP" skey="title" sort={sort} toggle={toggleSort} />
                <SortableTh label="Outlet" skey="outlet_name" sort={sort} toggle={toggleSort} className="hidden sm:table-cell" />
                <SortableTh label="Status" skey="status" sort={sort} toggle={toggleSort} align="center" />
                <SortableTh label="Media" skey="media_count" sort={sort} toggle={toggleSort} align="center" className="hidden sm:table-cell" />
                <SortableTh label="Ack" skey="completion_pct" sort={sort} toggle={toggleSort} align="right" />
                <SortableTh label="Overdue" skey="overdue" sort={sort} toggle={toggleSort} align="center" />
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {sortedSops.map((s) => (
                <tr key={s.sop_id} className="hover:bg-[#F7F5F2] cursor-pointer" onClick={() => setOpenSop(s.sop_id)} data-testid={`sop-row-${s.sop_id}`}>
                  <td className="px-3 py-2">
                    <p className="font-medium" style={{ color: '#1B2A4A' }}>{s.title}</p>
                    <p className="text-[10px]" style={{ color: '#6B7280' }}>{s.category}{s.version ? ` · v${s.version}` : ''}</p>
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell" style={{ color: '#6B7280' }}>{s.outlet_name}</td>
                  <td className="px-3 py-2 text-center">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{
                      backgroundColor: s.status === 'Active' ? '#ECFDF5' : s.status === 'Draft' ? '#FFFBEB' : '#F3F4F6',
                      color: s.status === 'Active' ? '#166534' : s.status === 'Draft' ? '#854F0B' : '#6B7280',
                    }}>{s.status}</span>
                  </td>
                  <td className="px-3 py-2 text-center hidden sm:table-cell">
                    {s.media_count > 0 ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                        {s.media_count} file{s.media_count === 1 ? '' : 's'}
                      </span>
                    ) : s.has_document ? (
                      <span className="text-[10px]" style={{ color: '#378ADD' }}>· doc</span>
                    ) : <span className="text-[10px]" style={{ color: '#D1D5DB' }}>—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-[11px] font-semibold tabular-nums" style={{ color: s.completion_pct >= 80 ? '#2D8B5E' : s.completion_pct >= 50 ? '#BA7517' : '#D4726A' }}>
                      {s.acknowledged}/{s.total_assigned}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {s.overdue > 0 ? (
                      <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>
                        <AlertTriangle size={9} /> {s.overdue}
                      </span>
                    ) : <span className="text-[10px]" style={{ color: '#D1D5DB' }}>—</span>}
                  </td>
                </tr>
              ))}
              {sortedSops.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-xs" style={{ color: '#6B7280' }}>
                  <p className="font-medium mb-1" style={{ color: '#1B2A4A' }}>{emptyHint()}</p>
                  <button onClick={clearFilters} className="underline mt-1" style={{ color: '#378ADD' }} data-testid="empty-clear">Clear all filters</button>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openSop && <SopDetailModal sopId={openSop} onClose={() => { setOpenSop(null); refresh(); }} />}
      {assignPerson && <AssignNewJoinerModal divisionId={divisionId} onClose={() => { setAssignPerson(false); refresh(); }} />}
    </div>
  );
}


// ─── SortableTh — reusable column header ────────────────────
function SortableTh({ label, skey, sort, toggle, align = 'left', className = '' }) {
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const active = sort.key === skey;
  return (
    <th className={`${alignCls} px-3 py-2 font-medium select-none cursor-pointer hover:bg-[#F0EAE2] ${className}`}
      style={{ color: active ? '#1B2A4A' : '#6B7280' }}
      onClick={() => toggle(skey)}
      data-testid={`th-${skey}`}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {active && (sort.dir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </span>
    </th>
  );
}


// ─── SOP Detail Modal ───────────────────────────────────────
function SopDetailModal({ sopId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [radioOpen, setRadioOpen] = useState(false);
  useEffect(() => {
    api.get(`/outlet-intel/training/sop/${sopId}`).then(r => setDetail(r.data)).catch(() => {});
  }, [sopId]);

  const ackedList = useMemo(() => (detail?.assignees || []).filter(a => a.acknowledged), [detail]);
  const pendingList = useMemo(() => (detail?.assignees || []).filter(a => !a.acknowledged), [detail]);

  const sop = detail?.sop;
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/my-training?open=${sopId}`
    : `https://scaleboard.in/my-training?open=${sopId}`;

  const shareOnWhatsApp = () => {
    if (!sop) return;
    const title = sop.title || 'SOP';
    const category = sop.category ? ` · *${sop.category}*` : '';
    const msg = [
      `🎙️ *${title}*${category}`,
      '',
      `Listen in your language (Tamil / Hindi / Nepali / Kannada) on ScaleBoard:`,
      shareUrl,
      '',
      '— MindEscapes Training',
    ].join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      import('sonner').then(({ toast }) => toast.success('Link copied — paste anywhere'));
    } catch (_) {
      prompt('Copy this link:', shareUrl);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-2" onClick={onClose}>
      <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="sop-detail-modal">
        <div className="p-4 border-b flex items-start justify-between gap-2" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{detail?.sop?.title || 'Loading…'}</p>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              {detail?.sop?.category} · {detail?.sop?.outlet_name} {detail?.sop?.version ? ` · v${detail.sop.version}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {sop && (
              <>
                <button onClick={() => setRadioOpen(true)}
                  className="text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1 font-semibold"
                  style={{ borderColor: '#2A7B88', color: '#FFF', backgroundColor: '#2A7B88' }}
                  title="Send as a personal WhatsApp training drop (tokenized · tracked · no login for staff)"
                  data-testid="sop-send-radio"
                >
                  <Radio size={12} /> Radio
                </button>
                <button onClick={shareOnWhatsApp}
                  className="text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1 font-medium"
                  style={{ borderColor: '#86EFAC', color: '#166534', backgroundColor: '#ECFDF5' }}
                  title="Share this SOP on WhatsApp — recipient gets a link to listen in 4 languages"
                  data-testid="sop-share-whatsapp"
                >
                  <MessageCircle size={12} /> Share
                </button>
                <button onClick={copyLink}
                  className="text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1"
                  style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
                  title="Copy a link to this SOP"
                  data-testid="sop-share-copy"
                >
                  <Copy size={12} />
                </button>
              </>
            )}
            <button onClick={onClose} data-testid="sop-modal-close" className="ml-1"><X size={18} style={{ color: '#6B7280' }} /></button>
          </div>
        </div>
        {radioOpen && <RadioSendModal sopId={sopId} sopTitle={sop?.title || ''} onClose={() => setRadioOpen(false)} />}
        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {/* Inline Listen pills — CEO / Leaders can QA translated narration without self-assignment */}
          <SopListenPills sopId={sopId} />
          {detail?.sop?.content && (
            <div className="prose prose-sm max-w-none">
              <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: '#9CA3AF' }}>Content</p>
              <div className="whitespace-pre-wrap text-xs mt-1" style={{ color: '#1B2A4A' }}>{detail.sop.content}</div>
            </div>
          )}
          {(detail?.sop?.media?.length > 0 || detail?.sop?.document_url) && (
            <div>
              <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: '#9CA3AF' }}>Media & Documents</p>
              <div className="space-y-1.5 mt-1">
                {detail?.sop?.document_url && (
                  <a href={detail.sop.document_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border hover:bg-[#F7F5F2]" style={{ borderColor: '#E5E1DC', color: '#378ADD' }}>
                    <FileText size={12} /> Primary document <ExternalLink size={10} />
                  </a>
                )}
                {(detail?.sop?.media || []).map((m, i) => (
                  <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border hover:bg-[#F7F5F2]" style={{ borderColor: '#E5E1DC', color: '#378ADD' }}>
                    {mediaIcon(m.resource_type || m.type)} {m.name || m.url.split('/').pop()} <ExternalLink size={10} />
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <div className="rounded-lg p-2.5" style={{ backgroundColor: '#ECFDF5' }}>
              <p className="text-[10px] uppercase font-semibold" style={{ color: '#166534' }}>Acknowledged</p>
              <p className="text-xl font-bold" style={{ color: '#166534' }}>{detail?.counts.acknowledged || 0}</p>
            </div>
            <div className="rounded-lg p-2.5" style={{ backgroundColor: '#FEF2F2' }}>
              <p className="text-[10px] uppercase font-semibold" style={{ color: '#991B1B' }}>Pending</p>
              <p className="text-xl font-bold" style={{ color: '#991B1B' }}>{detail?.counts.pending || 0}</p>
            </div>
          </div>
          {pendingList.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: '#991B1B' }}>Pending · who hasn't read</p>
              <div className="space-y-1 mt-1">
                {pendingList.map(a => (
                  <div key={a.person_id} className="flex items-center justify-between text-xs">
                    <span style={{ color: '#1B2A4A' }}>{a.name}</span>
                    <span style={{ color: '#9CA3AF' }}>{a.role || a.department} · {a.outlet}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {ackedList.length > 0 && (
            <details className="pt-2">
              <summary className="text-[11px] uppercase tracking-wide font-semibold cursor-pointer" style={{ color: '#166534' }}>Acknowledged ({ackedList.length}) — click to expand</summary>
              <div className="space-y-1 mt-1">
                {ackedList.map(a => (
                  <div key={a.person_id} className="flex items-center justify-between text-xs">
                    <span style={{ color: '#1B2A4A' }}><CheckCircle2 size={11} className="inline -mt-0.5" style={{ color: '#2D8B5E' }} /> {a.name}</span>
                    <span style={{ color: '#9CA3AF' }}>{a.acknowledged_at ? a.acknowledged_at.slice(0, 10) : ''}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Assign to New Joiner Modal ─────────────────────────────
function AssignNewJoinerModal({ divisionId, onClose }) {
  const [people, setPeople] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/people?division_id=${divisionId}`).then(r => setPeople(r.data || []));
  }, [divisionId]);

  const loadPreview = async (pid) => {
    setSelected(pid);
    setResult(null);
    const r = await api.post(`/outlet-intel/training/assign-onboarding/${pid}`, { dry_run: true });
    setPreview(r.data);
  };

  const confirm = async () => {
    if (!selected) return;
    setAssigning(true);
    try {
      const r = await api.post(`/outlet-intel/training/assign-onboarding/${selected}`, {});
      setResult(r.data);
    } finally {
      setAssigning(false);
    }
  };

  const filtered = people.filter(p => (p.name || '').toLowerCase().includes(filter.toLowerCase()) || (p.role || '').toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-2" onClick={onClose}>
      <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="assign-new-joiner-modal">
        <div className="p-4 border-b flex items-start justify-between" style={{ borderColor: '#E5E1DC' }}>
          <div>
            <p className="text-sm font-semibold flex items-center gap-2" style={{ color: '#1B2A4A' }}>
              <UserPlus size={14} /> Assign to New Joiner
            </p>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>Pick a team member — we'll auto-assign Day-1 + Week-1 SOPs for their role.</p>
          </div>
          <button onClick={onClose}><X size={18} style={{ color: '#6B7280' }} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {result ? (
            <div className="rounded-lg p-4" style={{ backgroundColor: '#ECFDF5' }}>
              <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: '#166534' }}>
                <CheckCircle2 size={14} /> Assigned {result.assigned_count} SOPs to {result.person.name}
              </p>
              <p className="text-[11px] mt-1" style={{ color: '#166534' }}>
                Track: <strong>{result.track}</strong> · Day 1: {result.day1.length} · Week 1: {result.week1.length}
                {result.skipped_count > 0 ? ` · ${result.skipped_count} skipped (already assigned)` : ''}
              </p>
            </div>
          ) : !selected ? (
            <>
              <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search by name or role…" className="w-full px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} data-testid="assign-search" />
              <div className="space-y-1 max-h-[50vh] overflow-y-auto">
                {filtered.map(p => (
                  <button key={p.id} onClick={() => loadPreview(p.id)} className="w-full text-left flex items-center justify-between p-2 rounded hover:bg-[#F7F5F2] border" style={{ borderColor: '#F3F4F6' }} data-testid={`assign-person-${p.id}`}>
                    <div>
                      <p className="text-xs font-medium" style={{ color: '#1B2A4A' }}>{p.name}</p>
                      <p className="text-[10px]" style={{ color: '#6B7280' }}>{p.role} · {p.outlet || '—'}</p>
                    </div>
                    <span className="text-[10px]" style={{ color: '#378ADD' }}>Preview →</span>
                  </button>
                ))}
                {filtered.length === 0 && <p className="text-[11px] text-center py-4" style={{ color: '#9CA3AF' }}>No people match.</p>}
              </div>
            </>
          ) : (
            <>
              {preview && (
                <div className="space-y-2">
                  <div className="rounded p-2.5" style={{ backgroundColor: '#F7F5F2' }}>
                    <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{preview.person.name}</p>
                    <p className="text-[10px]" style={{ color: '#6B7280' }}>{preview.person.role} · {preview.person.outlet}</p>
                    <p className="text-[10px] mt-1"><span style={{ color: '#9CA3AF' }}>Auto-detected track:</span> <strong style={{ color: '#1B2A4A' }}>{preview.track}</strong></p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: '#166534' }}>Day 1 · {preview.day1.length} SOPs</p>
                    <div className="text-[11px] mt-1" style={{ color: '#6B7280' }}>
                      {preview.day1.map(s => <div key={s.id}>· {s.title} <span style={{ color: '#9CA3AF' }}>({s.category})</span></div>)}
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: '#378ADD' }}>Week 1 · {preview.week1.length} SOPs</p>
                    <div className="text-[11px] mt-1" style={{ color: '#6B7280' }}>
                      {preview.week1.map(s => <div key={s.id}>· {s.title} <span style={{ color: '#9CA3AF' }}>({s.category})</span></div>)}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {selected && !result && (
          <div className="p-3 border-t flex gap-2" style={{ borderColor: '#E5E1DC' }}>
            <button onClick={confirm} disabled={assigning} className="flex-1 py-1.5 rounded text-xs font-semibold text-white disabled:opacity-50" style={{ backgroundColor: '#2A7B88' }} data-testid="confirm-assign">
              <Users size={11} className="inline -mt-0.5" /> {assigning ? 'Assigning…' : `Confirm · assign ${(preview?.day1.length || 0) + (preview?.week1.length || 0)} SOPs`}
            </button>
            <button onClick={() => { setSelected(null); setPreview(null); }} className="px-3 py-1.5 rounded text-xs border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>Pick another</button>
          </div>
        )}
      </div>
    </div>
  );
}
